import { useMemo } from 'react';
import { create } from 'zustand';
import axios from '../http/httpClient.js';
import {
    getJuryPointsConfiguration,
    getJuryVotingType,
    normalizeAchievementsSummary,
    sortFundingStages,
    transformLegacyPhases,
    transformPhases,
    type AchievementSummaryItem,
    type AchievementsSummary,
    type FundingStage,
    type JuryPointsConfiguration,
    type JuryVotingType,
    type LegacyPhaseWindows,
    type ProcessedPhases,
    type StagePhase,
} from '@core/business-logic';

/**
 * Campaign phases as the two backend families deliver them: a stage list
 * (`phases: [{ start, end, type }]`) or a single vote window
 * (`phases: { campaignLaunch, voteStart, voteEnd }`).
 */
export type RawPhases = StagePhase[] | LegacyPhaseWindows;

export type FeatureFlagName = 'memoryGame';

export type FeatureFlags = Partial<Record<FeatureFlagName, boolean | null | undefined>>;

export interface JuryFundingOption {
    displayValue: string;
    amount: number;
}

/** Contact data for the jury pages (editable in the cockpit). */
export interface JuryContact {
    organization?: string;
    contactPerson?: string;
    address?: string;
    email?: string;
    phone?: string;
}

export interface SecurityCheckConfig {
    minDurationMs: number;
    maxDurationMs: number;
}

/**
 * Client configuration as `/config` returns it. The fields below `juryFundingOptions` are only
 * sent by the backend families that use them; the hooks fall back to neutral defaults.
 */
export interface RawClientConfiguration {
    _id: string;
    phases: RawPhases;
    /** Funding stages; only funding-pool campaigns configure them. */
    funding?: FundingStage[];
    featureFlags: FeatureFlags;
    finalistCount?: number;
    joinedCategories?: boolean;
    juryFundingOptions?: JuryFundingOption[];
    juryVotingType?: JuryVotingType;
    juryPointsConfiguration?: JuryPointsConfiguration;
    juryContact?: JuryContact;
    isJuryVisible: boolean;
    securityCheck?: SecurityCheckConfig;
}

export interface ClientConfig extends Omit<RawClientConfiguration, 'phases'> {
    phases: ProcessedPhases;
    funding: FundingStage[];
}

interface ConfigState {
    config: ClientConfig | null;
    /** Product capabilities that `/config` does not describe; see `configureFeatures`. */
    features: ConfigFeatures;
    /** Balance of the campaign funding pool; stays 0 for campaigns without one. */
    fundingPoolBalance: number;
    /** Aggregated achievements keyed by type; stays empty for campaigns without them. */
    achievementsSummary: AchievementsSummary;
    loading: boolean;
    error: Error | null;
}

interface ConfigActions {
    fetchConfig: () => Promise<void>;
    /**
     * Enables the optional resources that cannot be derived from the `/config` payload. The app
     * shell calls this once from the product descriptor; everything defaults to off.
     */
    configureFeatures: (features?: Partial<ConfigFeatures>) => Promise<void>;
}

export type ConfigStore = ConfigState & ConfigActions;

/**
 * Capabilities of the backend family behind an app. They are explicit opt-ins instead of payload
 * probes, because a payload field only happens to correlate with the capability.
 */
export interface ConfigFeatures {
    /** Whether `GET /achievements/summary` exists and should be fetched. */
    achievements: boolean;
}

const DEFAULT_CONFIG_FEATURES: ConfigFeatures = { achievements: false };

/**
 * Funding-pool backends advertise their funding stages in `/config`; the competition-ranking
 * backend sends a stage list instead and serves no funding-pool route. The payload field stays the
 * probe for the funding balance.
 */
const isFundingPoolCampaign = (config: RawClientConfiguration): boolean => Array.isArray(config.funding);

/** Picks the phase adapter from the payload shape, not from the product. */
const processPhases = (phases: RawPhases | null | undefined): ProcessedPhases =>
    Array.isArray(phases) ? transformPhases(phases) : transformLegacyPhases(phases);

const toClientConfig = (raw: RawClientConfiguration): ClientConfig => ({
    ...raw,
    phases: processPhases(raw.phases),
    funding: sortFundingStages(raw.funding ?? []),
});

const fetchFundingPoolBalance = async (): Promise<number> => {
    try {
        const response = await axios.get<{ balance: number }>('/accounts/funding-pool');
        return response.data?.balance ?? 0;
    } catch (err) {
        console.error('Failed to fetch funding pool balance:', err);
        return 0;
    }
};

const fetchAchievementsSummary = async (): Promise<AchievementsSummary> => {
    try {
        const response = await axios.get<AchievementSummaryItem[] | AchievementsSummary>('/achievements/summary');
        return normalizeAchievementsSummary(response.data);
    } catch (err) {
        console.error('Failed to fetch achievements summary:', err);
        return {};
    }
};

export const useConfigStore = create<ConfigStore>((set, get) => ({
    config: null,
    features: DEFAULT_CONFIG_FEATURES,
    fundingPoolBalance: 0,
    achievementsSummary: {},
    loading: true,
    error: null,

    configureFeatures: async (features = {}) => {
        const previous = get().features;
        const next = { ...previous, ...features };
        set({ features: next });

        // The app shell renders below <ConfigProvider>, i.e. after the first `/config` round. A
        // late opt-in therefore has to fetch the summary itself instead of waiting for a refetch.
        if (next.achievements && !previous.achievements && get().config) {
            set({ achievementsSummary: await fetchAchievementsSummary() });
        }
    },

    fetchConfig: async () => {
        try {
            set({ loading: true, error: null });
            const response = await axios.get<RawClientConfiguration>('/config');

            // Both optional resources are fetched in the same round as the config, so consumers
            // never render a stale balance. The funding pool hangs on the payload, the
            // achievements on the product's explicit opt-in.
            const [fundingPoolBalance, achievementsSummary]: [number, AchievementsSummary] = await Promise.all([
                isFundingPoolCampaign(response.data) ? fetchFundingPoolBalance() : Promise.resolve(0),
                get().features.achievements ? fetchAchievementsSummary() : Promise.resolve({}),
            ]);

            set({
                config: toClientConfig(response.data),
                fundingPoolBalance,
                achievementsSummary,
                loading: false,
            });
        } catch (err) {
            console.error('Failed to fetch configuration:', err);
            set({
                error: err instanceof Error ? err : new Error(String(err)),
                loading: false,
            });
        }
    },
}));

/**
 * Helper to safely get the client config and throw an error if not yet loaded.
 */
const useSafeConfig = (): ClientConfig => {
    const config = useConfigStore((state) => state.config);
    if (!config) {
        throw new Error('ConfigStore is null. Make sure the configuration finished loading.');
    }
    return config;
};

// Strongly-typed hooks to expose specific configuration properties
export const usePhases = (): ProcessedPhases => useSafeConfig().phases;

export const useFundingStages = (): FundingStage[] => useSafeConfig().funding;

export const useFundingPoolBalance = (): number => useConfigStore((state) => state.fundingPoolBalance);

export const useAchievementsSummary = (): AchievementsSummary => useConfigStore((state) => state.achievementsSummary);

export const useFeatureFlags = (): FeatureFlags => useSafeConfig().featureFlags ?? {};

export const useFeatureFlag = (flag: FeatureFlagName): boolean => {
    const config = useSafeConfig();
    return Boolean(config.featureFlags?.[flag] === true);
};

export const useFinalistCount = (): number => useSafeConfig().finalistCount ?? 0;

export const useJoinedCategories = (): boolean => useSafeConfig().joinedCategories ?? false;

export const useIsJuryVisible = (): boolean => useSafeConfig().isJuryVisible;

export const useIsMemoryGameActive = (): boolean => {
    return useFeatureFlag('memoryGame');
};

export const useJuryFundingOptions = (): JuryFundingOption[] => useSafeConfig().juryFundingOptions ?? [];

/** Active voting type of the jury; falls back to funding. */
export const useJuryVotingType = (): JuryVotingType => getJuryVotingType(useSafeConfig());

/** Points configuration including backend defaults for missing fields. */
export const useJuryPointsConfiguration = (): JuryPointsConfiguration => getJuryPointsConfiguration(useSafeConfig());

/**
 * Contact data for the jury pages. The values come entirely from the configuration; the defaults
 * live in the backend model. If the block is missing, the fields stay empty and the jury page hides
 * the contact section.
 */
export const useJuryContact = (): Required<JuryContact> => {
    const { juryContact } = useSafeConfig();
    return useMemo(
        () => ({
            organization: juryContact?.organization ?? '',
            contactPerson: juryContact?.contactPerson ?? '',
            address: juryContact?.address ?? '',
            email: juryContact?.email ?? '',
            phone: juryContact?.phone ?? '',
        }),
        [juryContact]
    );
};

export const DEFAULT_SECURITY_CHECK_MIN_MS = 3000;
export const DEFAULT_SECURITY_CHECK_MAX_MS = 6000;

export const useSecurityCheckConfig = (): SecurityCheckConfig => {
    const config = useConfigStore((state) => state.config);
    const min = config?.securityCheck?.minDurationMs ?? DEFAULT_SECURITY_CHECK_MIN_MS;
    const max = config?.securityCheck?.maxDurationMs ?? DEFAULT_SECURITY_CHECK_MAX_MS;
    return { minDurationMs: min, maxDurationMs: Math.max(min, max) };
};
